import React from "react";
import { Topic, P, H3, H4, UL, Callout, Table, VisualBox } from "@/pages/knowledge/Atoms";

export default function Options({ GreeksGrid, OptionChainViz }) {
  return (
    <>
      <Topic id="option-greeks" title="What are Option Greeks?">
        <P>
          When you trade options — whether Nifty, Bank Nifty or stock options —
          prices change every second. But <b>why</b> do they change? That&rsquo;s
          where Option Greeks come in. They help you understand how an
          option&rsquo;s price (premium) changes with market movement, time decay
          and volatility.
        </P>
        <Callout>
          <b>Option Greeks = the heartbeat of options trading.</b> They are
          mathematical indicators that help measure risk, control exposure and
          plan adjustments.
        </Callout>

        <H3>The 5 main Greeks</H3>
        <GreeksGrid />

        <Table
          headers={["Greek", "What it measures", "Simple meaning", "Effect on price"]}
          rows={[
            ["Delta (Δ)", "Price movement", "Change in option price per ₹1 move in underlying", "How closely option follows underlying"],
            ["Gamma (Γ)", "Delta's movement", "How much Delta itself changes per ₹1 move", "How fast option reacts"],
            ["Theta (Θ)", "Time decay", "Daily value lost as expiry approaches", "How time affects P&L"],
            ["Vega (ν)", "Volatility impact", "Change in price for 1% volatility change", "Market fear/excitement"],
            ["Rho (ρ)", "Interest rate impact", "Effect of rate changes", "Mostly for long-dated options"],
          ]}
        />

        <H3>Example — Nifty 26,000 Call</H3>
        <P>Nifty = 26,000 and you buy a 26,000 CE @ ₹100.</P>
        <Table
          headers={["Event", "What happens", "Why"]}
          rows={[
            ["Nifty rises to 26,100", "Premium → ₹130", "Delta effect"],
            ["Nifty rises quickly", "Delta itself rises faster", "Gamma effect"],
            ["5 days pass, no movement", "Premium → ₹80", "Theta — time decay"],
            ["Market becomes volatile", "Premium → ₹120", "Vega — volatility up"],
          ]}
        />
        <Callout kind="info">
          Even if the price doesn&rsquo;t move, your premium can — because the
          Greeks are working in the background.
        </Callout>

        <H3>Real-life analogy — your car dashboard</H3>
        <UL>
          <li><b>Delta</b> → speed (how fast your trade moves with the market)</li>
          <li><b>Gamma</b> → acceleration (how quickly it reacts)</li>
          <li><b>Theta</b> → fuel drain (premium decay)</li>
          <li><b>Vega</b> → weather (market volatility)</li>
          <li><b>Rho</b> → road slope (interest rates)</li>
        </UL>
      </Topic>

      <Topic id="option-chain" title="What is an Option Chain?">
        <P>
          An Option Chain is like a detailed report card of the options market.
          It shows all available Call (CE) and Put (PE) options for a stock or
          index — with prices, strike levels, open interest, and more. For Nifty
          and Bank Nifty traders, it&rsquo;s where every trade starts.
        </P>

        <H3>Example: Nifty 26,000 Option Chain Snapshot</H3>
        <OptionChainViz />
        <p className="text-xs text-[#94a3b8] mt-2 mb-6 font-mono">
          * Indicative data for explanation only.
        </p>

        <H3>How to read the option chain</H3>
        <H4>1. Identify the At-the-Money (ATM) strike</H4>
        <P>Closest strike to current price. Nifty = 26,000 → ATM = 26,000.</P>
        <H4>2. Check Open Interest (OI)</H4>
        <UL>
          <li>High Call OI → traders see that level as <b>resistance</b>.</li>
          <li>High Put OI → traders see that level as <b>support</b>.</li>
        </UL>
        <H4>3. Look for intraday changes in OI</H4>
        <UL>
          <li>OI ↑ + Price ↑ → <b>Long buildup</b> (bullish)</li>
          <li>OI ↑ + Price ↓ → <b>Short buildup</b> (bearish)</li>
          <li>OI ↓ + Price ↑ → <b>Short covering</b> (bullish)</li>
          <li>OI ↓ + Price ↓ → <b>Long unwinding</b> (bearish)</li>
        </UL>
        <H4>4. Observe Implied Volatility (IV)</H4>
        <UL>
          <li>High IV → more uncertainty → higher option prices.</li>
          <li>Low IV → calmer market → lower option prices.</li>
        </UL>

        <Callout kind="success">
          <b>Practical example:</b> If Nifty is at 26,000, highest Call OI at
          26,200 and highest Put OI at 25,800 — traders expect Nifty to stay
          between 25,800 and 26,200 in the near term. Useful for range-based
          straddles and strangles.
        </Callout>
      </Topic>
    </>
  );
}
